import React, { useState } from "react";
import { Paperclip, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const SERVICE_OPTIONS = [
  "Start-Up / Launch", "Growth Strategy", "Marketing", "Lead Generation",
  "Contract / Tender Support", "Website", "Recruitment", "Operations",
  "Administration", "AI / Automation", "Fractional Support", "Other",
];
const STAGES = ["Just an idea", "Pre-launch", "Newly launched (0–1 yr)", "Established (1–3 yrs)", "Growing (3+ yrs)", "Scaling / expanding"];
const TYPES = ["Existing business", "New idea"];
const BUDGETS = ["Not sure yet", "Under £1,000", "£1,000 – £5,000", "£5,000 – £15,000", "£15,000+", "Monthly retainer"];

const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-bd-gold outline-none py-3 text-bd-ivory placeholder:text-white/35 transition-colors";
const labelCls = "overline block mb-1 text-bd-ivory/50";

export default function BusinessEnquiryForm() {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", business_stage: STAGES[0],
    industry: "", business_type: TYPES[0], main_problem: "", budget_range: BUDGETS[0],
    start_date: "", message: "", services_required: [],
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleService = (s) =>
    setForm((f) => ({ ...f, services_required: f.services_required.includes(s) ? f.services_required.filter((x) => x !== s) : [...f.services_required, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.main_problem) {
      toast.error("Please complete your name, email and the main problem you'd like help with.");
      return;
    }
    setBusy(true);
    try {
      let attachment_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/enquiry", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_id = data.id;
      }
      await api.post("/enquiries", { ...form, category: "Business Services", attachment_id });
      setDone(true);
      toast.success("Thank you — your enquiry has been received.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="business-enquiry-success" className="text-center py-12 text-bd-ivory">
        <CheckCircle2 className="mx-auto mb-4 text-bd-gold" size={48} strokeWidth={1} />
        <h3 className="font-serif text-3xl mb-3">Enquiry received</h3>
        <p className="text-bd-ivory/60 max-w-md mx-auto">Thank you. We'll review your business and be in touch to arrange a consultation.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="business-enquiry-form" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input data-testid="bs-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
        <input data-testid="bs-company" className={inputCls} placeholder="Company (or working name)" value={form.company} onChange={set("company")} />
        <input data-testid="bs-email" type="email" className={inputCls} placeholder="Email address *" value={form.email} onChange={set("email")} />
        <input data-testid="bs-phone" className={inputCls} placeholder="Telephone" value={form.phone} onChange={set("phone")} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={labelCls}>Business stage</label>
          <select data-testid="bs-stage" value={form.business_stage} onChange={set("business_stage")} className={inputCls}>
            {STAGES.map((s) => <option key={s} value={s} className="text-black">{s}</option>)}
          </select>
        </div>
        <input data-testid="bs-industry" className={inputCls} placeholder="Industry / sector" value={form.industry} onChange={set("industry")} />
        <div>
          <label className={labelCls}>Existing business or new idea</label>
          <select data-testid="bs-type" value={form.business_type} onChange={set("business_type")} className={inputCls}>
            {TYPES.map((t) => <option key={t} value={t} className="text-black">{t}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Budget range</label>
          <select data-testid="bs-budget" value={form.budget_range} onChange={set("budget_range")} className={inputCls}>
            {BUDGETS.map((b) => <option key={b} value={b} className="text-black">{b}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>Services required</label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
          {SERVICE_OPTIONS.map((s) => {
            const on = form.services_required.includes(s);
            return (
              <button type="button" key={s} data-testid={`bs-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => toggleService(s)}
                className={`text-left text-xs px-3 py-2.5 border transition-colors ${on ? "border-bd-gold text-bd-gold bg-bd-gold/5" : "border-white/15 text-bd-ivory/60 hover:border-white/40"}`}>
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <textarea data-testid="bs-problem" rows={3} className={inputCls} placeholder="What's the main problem or goal you'd like help with? *" value={form.main_problem} onChange={set("main_problem")} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
        <div>
          <label className={labelCls}>Desired start date</label>
          <input data-testid="bs-start" type="date" className={inputCls} value={form.start_date} onChange={set("start_date")} />
        </div>
        <label data-testid="bs-attachment" className="flex items-center gap-3 cursor-pointer text-sm text-bd-ivory/60 hover:text-bd-gold transition-colors py-3">
          <Paperclip size={16} strokeWidth={1.5} />
          {file ? file.name : "Attach a document (optional)"}
          <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.csv,.txt" />
        </label>
      </div>

      <textarea data-testid="bs-message" rows={4} className={inputCls} placeholder="Anything else you'd like us to know (optional)" value={form.message} onChange={set("message")} />

      <button type="submit" data-testid="bs-submit" disabled={busy}
        className="inline-flex items-center gap-2 bg-bd-gold text-bd-black hover:bg-bd-gold2 transition-colors duration-300 px-8 py-4 text-sm tracking-[0.15em] uppercase disabled:opacity-60">
        {busy && <Loader2 size={16} className="animate-spin" />} Request a Consultation
      </button>
      <p className="text-xs text-bd-ivory/35 leading-relaxed">
        We help you build the systems, processes and infrastructure to move your business forward. We do not guarantee revenue, funding, contracts or specific business outcomes.
      </p>
    </form>
  );
}
